import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useGetOne, useNotify, useRefresh } from 'react-admin';
import { DialogCloseButton } from '@app/misc/DialogCloseButton';
import type { ExpensesClaimSummary } from '@wwf/sdk';
import { ExpensesClaimDrawerForm } from './ExpensesClaimDrawerForm';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DrawerState } from '@app/misc/DrawerContext';
import { useDrawer } from '@app/misc/DrawerContext';
import { CopyAppLinkButton } from '@app/misc/CopyAppLinkButton';

type ExpensesClaimDrawerMode = 'create' | 'edit' | 'show';

export function useExpensesClaimDrawer() {
    const drawer = useDrawer();
    const openDrawer = useCallback(
        (
            mode: ExpensesClaimDrawerMode,
            selectedId: ExpensesClaimSummary['id'] | null
        ) =>
            drawer.openDrawer(state => (
                <ExpensesClaimDrawer
                    {...state}
                    mode={mode}
                    selectedId={selectedId}
                />
            )),
        [drawer]
    );
    const closeDrawer = useCallback(() => {
        drawer.closeDrawer();
    }, [drawer]);
    return { openDrawer, closeDrawer };
}

export function ExpensesClaimDrawer({
    open,
    onClose,
    mode,
    selectedId,
}: DrawerState & {
    mode: ExpensesClaimDrawerMode;
    selectedId: ExpensesClaimSummary['id'] | null;
}) {
    const notify = useNotify();
    const refresh = useRefresh();

    const [viewMode, setViewMode] = useState<ExpensesClaimDrawerMode>(mode);

    useEffect(() => {
        if (open) {
            setViewMode(mode);
        }
    }, [open, mode, selectedId]);

    const title = useMemo(() => {
        if (viewMode === 'create') return 'Create expenses claim';
        if (viewMode === 'show') return 'Expenses claim';
        return 'Edit expenses claim';
    }, [viewMode]);

    const loadRecord = viewMode === 'edit' || viewMode === 'show';

    const { data: selectedRecord, isPending: isPendingSelectedRecord } =
        useGetOne<ExpensesClaimSummary>(
            'expensesClaim',
            { id: selectedId ?? undefined },
            {
                enabled: open && loadRecord && selectedId != null,
            }
        );

    const canToggleShowEdit = !!selectedId && viewMode !== 'create';

    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            <DialogCloseButton onClose={onClose} />
            <Box p={3} pt={4}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                    gap={1}
                >
                    <Typography variant="h6">{title}</Typography>
                    {canToggleShowEdit &&
                        (viewMode === 'show' ? (
                            <Stack
                                direction={'row'}
                                mb={2}
                                flexWrap={'wrap'}
                                justifyContent={'space-between'}
                                gap={1}
                            >
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditIcon />}
                                    onClick={() => setViewMode('edit')}
                                >
                                    Edit
                                </Button>
                                <CopyAppLinkButton
                                    type="expensesClaim"
                                    id={String(selectedRecord?.id ?? '')}
                                />
                            </Stack>
                        ) : (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => setViewMode('show')}
                            >
                                View
                            </Button>
                        ))}
                </Stack>

                <ExpensesClaimDrawerForm
                    mode={viewMode}
                    record={loadRecord ? selectedRecord : undefined}
                    loading={loadRecord && isPendingSelectedRecord}
                    onClose={onClose}
                    onSuccess={() => {
                        refresh();
                        notify('Saved', { type: 'info' });
                    }}
                />
            </Box>
        </Box>
    );
}
